import { useState, useEffect, useCallback } from 'react';
import { BarChart2, Clock, TrendingUp, RefreshCw, AlertTriangle, CheckCircle, Info } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface SourceAnalytic {
  source: string;
  application_count: number;
  hire_count: number;
  avg_days_to_hire: number;
  offer_acceptance_rate: number;
  cost_per_hire_estimate: number;
}

interface TimeToHireData {
  overall: DeptTTH;
  by_department: DeptTTH[];
}

interface DeptTTH {
  department: string;
  count: number;
  applied_to_screening: number;
  screening_to_interview: number;
  interview_to_offer: number;
  offer_to_hired: number;
  total_avg: number;
}

interface OfferPrediction {
  candidate_id: string;
  candidate_name: string;
  status: string;
  days_in_pipeline: number;
  acceptance_probability: number;
  risk_factors: string[];
  recommended_actions: string[];
}

interface Candidate {
  id: string;
  first_name: string;
  last_name: string;
  status: string;
  job_id?: string;
}

// ─── Source Bar Chart ─────────────────────────────────────────

function SourceBarChart({ analytics }: { analytics: SourceAnalytic[] }) {
  const maxCount = Math.max(...analytics.map(a => a.application_count), 1);

  const SOURCE_LABELS: Record<string, string> = {
    linkedin: 'LinkedIn',
    referral: 'Referral',
    job_board: 'Job Board',
    company_website: 'Website',
    recruiter: 'Recruiter',
    other: 'Other',
  };

  return (
    <div className="space-y-3">
      {analytics.sort((a, b) => b.application_count - a.application_count).map(a => (
        <div key={a.source} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground font-medium">{SOURCE_LABELS[a.source] ?? a.source}</span>
            <div className="flex items-center gap-3 text-muted-foreground">
              <span>{a.hire_count} hired</span>
              <span className="text-green-400">{a.offer_acceptance_rate}% accept</span>
              <span className="text-amber-400">${a.cost_per_hire_estimate.toLocaleString()} CPH</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-6 bg-surface-3 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500/70 rounded-full flex items-center pl-2"
                style={{ width: `${Math.max(4, (a.application_count / maxCount) * 100)}%` }}
              >
                {a.application_count > 0 && (
                  <span className="text-xs text-foreground font-medium">{a.application_count}</span>
                )}
              </div>
            </div>
            <span className="text-xs text-muted-foreground w-16 text-right">{a.avg_days_to_hire}d avg</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Funnel Chart ─────────────────────────────────────────────

function FunnelChart({ data }: { data: TimeToHireData }) {
  const stages = [
    { key: 'applied_to_screening', label: 'Applied → Screening' },
    { key: 'screening_to_interview', label: 'Screening → Interview' },
    { key: 'interview_to_offer', label: 'Interview → Offer' },
    { key: 'offer_to_hired', label: 'Offer → Hired' },
  ];

  const overall = data.overall;
  const maxDays = Math.max(
    overall.applied_to_screening,
    overall.screening_to_interview,
    overall.interview_to_offer,
    overall.offer_to_hired,
    1,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-sm">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Clock className="w-4 h-4" />
          Total avg: <span className="text-foreground font-medium">{overall.total_avg} days</span>
        </div>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{overall.count} hires</span>
      </div>

      <div className="space-y-3">
        {stages.map(stage => {
          const days = overall[stage.key as keyof DeptTTH] as number;
          const width = maxDays > 0 ? Math.max(8, (days / maxDays) * 100) : 8;
          return (
            <div key={stage.key} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{stage.label}</span>
                <span className="text-foreground font-medium">{days}d</span>
              </div>
              <div className="h-8 bg-surface-3 rounded-lg overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500/80 to-amber-600/40 rounded-lg flex items-center pl-3"
                  style={{ width: `${width}%` }}
                >
                  <span className="text-xs text-foreground font-medium">{days}d</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {data.by_department.length > 0 && (
        <div className="pt-3 border-t border-border">
          <h3 className="text-xs font-semibold text-muted-foreground mb-3">By Department</h3>
          <div className="space-y-2">
            {data.by_department.map(dept => (
              <div key={dept.department} className="flex items-center justify-between text-xs">
                <span className="text-foreground">{dept.department}</span>
                <div className="flex items-center gap-4 text-muted-foreground">
                  <span>{dept.count} hires</span>
                  <span className="text-amber-400 font-medium">{dept.total_avg}d avg</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Probability Badge ────────────────────────────────────────

function ProbabilityBadge({ pct }: { pct: number }) {
  const color = pct >= 70 ? 'bg-green-500/20 text-green-400' : pct >= 40 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{pct}%</span>;
}

// ─── Main Page ────────────────────────────────────────────────

export function CandidateSourcingPage() {
  const [activeTab, setActiveTab] = useState<'analytics' | 'tth' | 'predictions'>('analytics');
  const [analytics, setAnalytics] = useState<SourceAnalytic[]>([]);
  const [tthData, setTTHData] = useState<TimeToHireData | null>(null);
  const [predictions, setPredictions] = useState<OfferPrediction[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selectedPrediction, setSelectedPrediction] = useState<OfferPrediction | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPred, setLoadingPred] = useState(false);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/sourcing/analytics');
      if (res.ok) {
        const data = await res.json() as { analytics: SourceAnalytic[] };
        setAnalytics(data.analytics ?? []);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  const loadTTH = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/sourcing/time-to-hire');
      if (res.ok) {
        const data = await res.json() as TimeToHireData;
        setTTHData(data);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  const loadCandidates = useCallback(async () => {
    try {
      const res = await fetch('/api/candidates?status=offer&limit=50');
      if (res.ok) {
        const data = await res.json() as { candidates: Candidate[] };
        setCandidates(data.candidates ?? []);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics') void loadAnalytics();
    if (activeTab === 'tth') void loadTTH();
    if (activeTab === 'predictions') void loadCandidates();
  }, [activeTab, loadAnalytics, loadTTH, loadCandidates]);

  async function loadPrediction(candidateId: string) {
    setLoadingPred(true);
    try {
      const res = await fetch(`/api/people/sourcing/offer-prediction/${candidateId}`);
      if (res.ok) {
        const data = await res.json() as OfferPrediction;
        setPredictions(prev => {
          const idx = prev.findIndex(p => p.candidate_id === candidateId);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = data;
            return updated;
          }
          return [...prev, data];
        });
        setSelectedPrediction(data);
      }
    } catch { /* silent */ } finally {
      setLoadingPred(false);
    }
  }

  const TABS = [
    { id: 'analytics' as const, label: 'Source Analytics', icon: BarChart2 },
    { id: 'tth' as const, label: 'Time to Hire', icon: Clock },
    { id: 'predictions' as const, label: 'Offer Predictions', icon: TrendingUp },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">Candidate Sourcing Intelligence</h1>
        </div>
        <button
          onClick={() => {
            if (activeTab === 'analytics') void loadAnalytics();
            if (activeTab === 'tth') void loadTTH();
          }}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-3"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-3 rounded-lg p-1 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm transition-colors ${
              activeTab === tab.id ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Source Analytics */}
      {activeTab === 'analytics' && (
        <div className="glass rounded-xl p-6">
          <h2 className="text-sm font-semibold text-foreground mb-6">Pipeline Velocity by Source</h2>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading analytics...</p>
          ) : analytics.length === 0 ? (
            <div className="text-center py-8">
              <BarChart2 className="w-10 h-10 text-amber-500/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No candidate data yet. Add candidates to see sourcing analytics.</p>
            </div>
          ) : (
            <>
              <SourceBarChart analytics={analytics} />
              <div className="mt-6 pt-4 border-t border-border grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-foreground">{analytics.reduce((s, a) => s + a.application_count, 0)}</p>
                  <p className="text-xs text-muted-foreground">Total Applications</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-400">{analytics.reduce((s, a) => s + a.hire_count, 0)}</p>
                  <p className="text-xs text-muted-foreground">Total Hires</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">
                    {analytics.reduce((s, a) => s + a.application_count, 0) > 0
                      ? Math.round((analytics.reduce((s, a) => s + a.hire_count, 0) / analytics.reduce((s, a) => s + a.application_count, 0)) * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Overall Hire Rate</p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Time to Hire */}
      {activeTab === 'tth' && (
        <div className="glass rounded-xl p-6">
          <h2 className="text-sm font-semibold text-foreground mb-6">Average Days per Hiring Stage</h2>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : !tthData ? (
            <div className="text-center py-8">
              <Clock className="w-10 h-10 text-amber-500/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No hiring data available yet.</p>
            </div>
          ) : (
            <FunnelChart data={tthData} />
          )}
        </div>
      )}

      {/* Offer Predictions */}
      {activeTab === 'predictions' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 glass rounded-xl p-6">
            <h2 className="text-sm font-semibold text-foreground mb-4">Candidates in Offer Stage</h2>
            {candidates.length === 0 ? (
              <div className="text-center py-8">
                <TrendingUp className="w-10 h-10 text-amber-500/20 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No candidates in offer stage.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {candidates.map(c => {
                  const pred = predictions.find(p => p.candidate_id === c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => pred ? setSelectedPrediction(pred) : void loadPrediction(c.id)}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-surface-3 hover:bg-surface-2 text-left transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">{c.first_name} {c.last_name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{c.status}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {pred ? (
                          <ProbabilityBadge pct={pred.acceptance_probability} />
                        ) : loadingPred ? (
                          <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                        ) : (
                          <span className="text-xs text-amber-400">Predict →</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Risk Factors Panel */}
          {selectedPrediction && (
            <div className="glass rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">{selectedPrediction.candidate_name}</h3>
                <ProbabilityBadge pct={selectedPrediction.acceptance_probability} />
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5" />
                {selectedPrediction.days_in_pipeline} days in pipeline
              </div>

              {selectedPrediction.risk_factors.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-red-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Risk Factors
                  </h4>
                  {selectedPrediction.risk_factors.map((r, i) => (
                    <p key={i} className="text-xs text-foreground/90 pl-4 border-l border-red-500/30">{r}</p>
                  ))}
                </div>
              )}

              {selectedPrediction.recommended_actions.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-green-400 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Recommended Actions
                  </h4>
                  {selectedPrediction.recommended_actions.map((a, i) => (
                    <p key={i} className="text-xs text-foreground/90 pl-4 border-l border-green-500/30">{a}</p>
                  ))}
                </div>
              )}

              <div className="p-2 rounded-lg bg-surface-3 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">Prediction powered by AI based on pipeline signals. Not a guarantee.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
